import React, { useContext, useEffect, useState, useCallback } from 'react';
import { AppContext } from '../../App';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import SuperAdminLayout from '../../components/SuperAdminLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Mail, Send, CheckCircle, XCircle, 
  RefreshCw, Settings, History, BarChart3,
  ExternalLink, AlertCircle, Zap, ChevronRight
} from 'lucide-react';

// Helper to get auth config for axios requests
const getAuthConfig = () => {
  const token = localStorage.getItem('session_token');
  return {
    withCredentials: true,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  };
};

export default function SuperAdminEmail() {
  const { user, API } = useContext(AppContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [emailConfig, setEmailConfig] = useState({
    provider: 'sendgrid',
    from_email: '',
    from_name: 'Survivor Fantasy League',
    is_configured: false,
    api_key_set: false,
    last_tested_at: null,
    last_test_status: null
  });
  const [stats, setStats] = useState({
    total: 0,
    sent: 0,
    failed: 0,
    by_type: { invites: 0, tests: 0 },
    recent_7_days: 0
  });
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);

  // Test email state
  const [showTestDialog, setShowTestDialog] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testSubject, setTestSubject] = useState('Test Email from Survivor Fantasy League');
  const [testMessage, setTestMessage] = useState('This is a test email to verify your SendGrid configuration is working correctly.');
  const [sending, setSending] = useState(false);

  // Config edit state
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [editFromEmail, setEditFromEmail] = useState('');
  const [editFromName, setEditFromName] = useState('');
  const [savingConfig, setSavingConfig] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [configRes, statsRes] = await Promise.all([
        axios.get(`${API}/platform/email-config`, getAuthConfig()),
        axios.get(`${API}/platform/email/stats`, getAuthConfig())
      ]);
      
      if (configRes.data) {
        setEmailConfig(configRes.data);
        setEditFromEmail(configRes.data.from_email || '');
        setEditFromName(configRes.data.from_name || 'Survivor Fantasy League');
      }
      if (statsRes.data) setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to load email config:', error);
      toast.error('Failed to load email configuration');
    } finally {
      setLoading(false);
    }
  }, [API]);

  const loadLogs = useCallback(async () => {
    try {
      setLogsLoading(true);
      const res = await axios.get(`${API}/platform/email/logs?limit=50`, getAuthConfig());
      setLogs(res.data?.logs || []);
    } catch (error) {
      console.error('Failed to load email logs:', error);
    } finally {
      setLogsLoading(false);
    }
  }, [API]);

  useEffect(() => {
    if (!user?.is_super_admin) {
      navigate('/home');
      return;
    }
    loadData();
  }, [user, navigate, loadData]);

  const testConnection = async () => {
    try {
      const res = await axios.post(`${API}/platform/email/test-connection`, {}, getAuthConfig());
      if (res.data.success) {
        toast.success('SendGrid connection successful!');
        loadData();
      } else {
        toast.error(`Connection failed: ${res.data.error}`);
      }
    } catch (error) {
      toast.error('Failed to test connection');
    }
  };

  const sendTestEmail = async () => {
    if (!testEmail) {
      toast.error('Please enter an email address');
      return;
    }
    
    try {
      setSending(true);
      await axios.post(`${API}/platform/email/test`, {
        to_email: testEmail,
        subject: testSubject,
        message: testMessage
      }, getAuthConfig());
      
      toast.success(`Test email sent to ${testEmail}`);
      setShowTestDialog(false);
      setTestEmail('');
      loadData();
      loadLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setSending(false);
    }
  };

  const saveConfig = async () => {
    try {
      setSavingConfig(true);
      await axios.patch(`${API}/platform/email-config`, {
        from_email: editFromEmail,
        from_name: editFromName
      }, getAuthConfig());
      
      toast.success('Email configuration updated');
      setShowConfigDialog(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save configuration');
    } finally {
      setSavingConfig(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleString();
  };

  if (loading) {
    return (
      <SuperAdminLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-amber-500" />
        </div>
      </SuperAdminLayout>
    );
  }

  return (
    <SuperAdminLayout>
      <div className="max-w-4xl mx-auto space-y-4" data-testid="super-admin-email">
        {/* Header */}
        <div className="flex items-center justify-between gap-4 mb-2">
          <div className="min-w-0">
            <h1 className="text-lg lg:text-2xl font-bold text-white">Email Configuration</h1>
            <p className="text-xs lg:text-sm text-zinc-400">Manage SendGrid integration</p>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowConfigDialog(true)}
              data-testid="edit-config-btn"
            >
              <Settings className="w-4 h-4 lg:mr-1" />
              <span className="hidden lg:inline">Settings</span>
            </Button>
            <Button
              size="sm"
              onClick={() => setShowTestDialog(true)}
              className="bg-amber-500 hover:bg-amber-600"
              data-testid="send-test-email-btn"
            >
              <Send className="w-4 h-4 lg:mr-1" />
              <span className="hidden lg:inline">Test Email</span>
            </Button>
          </div>
        </div>

        {/* Email Triggers Quick Link */}
        <Link to="/super-admin/email/triggers">
          <Card className="bg-gradient-to-r from-violet-900/50 to-purple-900/50 border-violet-700 hover:border-violet-500 transition-colors cursor-pointer">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-violet-800 rounded-lg flex items-center justify-center">
                    <Zap className="w-5 h-5 text-violet-300" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">Email Triggers</h3>
                    <p className="text-violet-300 text-sm">Configure which events send automatic emails</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-violet-400" />
              </div>
            </CardContent>
          </Card>
        </Link>

        {/* Drip Campaigns Quick Link */}
        <Link to="/super-admin/email/drip-campaigns">
          <Card className="bg-gradient-to-r from-emerald-900/50 to-teal-900/50 border-emerald-700 hover:border-emerald-500 transition-colors cursor-pointer">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-800 rounded-lg flex items-center justify-center">
                    <Mail className="w-5 h-5 text-emerald-300" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">Drip Campaigns</h3>
                    <p className="text-emerald-300 text-sm">Automated email sequences for user activation</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-emerald-400" />
              </div>
            </CardContent>
          </Card>
        </Link>

        {/* Status Card */}
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-white">
              <Mail className="w-5 h-5 text-amber-500" />
              SendGrid Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="space-y-1">
                <p className="text-sm text-zinc-400">Status</p>
                <div className="flex items-center gap-2">
                  {emailConfig.is_configured ? (
                    <>
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span className="text-green-400 font-medium">Configured</span>
                    </>
                  ) : (
                    <>
                      <XCircle className="w-5 h-5 text-red-500" />
                      <span className="text-red-400 font-medium">Not Configured</span>
                    </>
                  )}
                </div>
              </div>
              
              <div className="space-y-1">
                <p className="text-sm text-zinc-400">API Key</p>
                <Badge variant={emailConfig.api_key_set ? "success" : "destructive"}>
                  {emailConfig.api_key_set ? "Set" : "Not Set"}
                </Badge>
              </div>
              
              <div className="space-y-1">
                <p className="text-sm text-zinc-400">From Email</p>
                <p className="text-white font-mono text-sm">
                  {emailConfig.from_email || 'Not configured'}
                </p>
              </div>
              
              <div className="space-y-1">
                <p className="text-sm text-zinc-400">Last Test</p>
                <div className="flex items-center gap-2">
                  {emailConfig.last_test_status === 'success' ? (
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  ) : emailConfig.last_test_status === 'failed' ? (
                    <XCircle className="w-4 h-4 text-red-500" />
                  ) : (
                    <AlertCircle className="w-4 h-4 text-zinc-500" />
                  )}
                  <span className="text-zinc-300 text-sm">
                    {emailConfig.last_tested_at 
                      ? formatDate(emailConfig.last_tested_at)
                      : 'Never tested'}
                  </span>
                </div>
              </div>
            </div>
            
            <div className="mt-4 pt-4 border-t border-zinc-800 flex items-center justify-between">
              <a 
                href="https://app.sendgrid.com/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-amber-500 hover:text-amber-400 flex items-center gap-1 text-sm"
              >
                Open SendGrid Dashboard
                <ExternalLink className="w-4 h-4" />
              </a>
              <Button variant="outline" size="sm" onClick={testConnection}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Test Connection
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Tabs for Stats and Logs */}
        <Tabs defaultValue="stats" className="space-y-4">
          <TabsList className="bg-zinc-900 border-zinc-800">
            <TabsTrigger value="stats" className="data-[state=active]:bg-amber-500">
              <BarChart3 className="w-4 h-4 mr-2" />
              Statistics
            </TabsTrigger>
            <TabsTrigger 
              value="logs" 
              className="data-[state=active]:bg-amber-500"
              onClick={loadLogs}
            >
              <History className="w-4 h-4 mr-2" />
              Email Logs
            </TabsTrigger>
          </TabsList>

          <TabsContent value="stats">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="bg-zinc-900 border-zinc-800">
                <CardContent className="pt-6">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-white">{stats.total}</p>
                    <p className="text-zinc-400 text-sm mt-1">Total Emails</p>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-zinc-900 border-zinc-800">
                <CardContent className="pt-6">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-green-400">{stats.sent}</p>
                    <p className="text-zinc-400 text-sm mt-1">Sent Successfully</p>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-zinc-900 border-zinc-800">
                <CardContent className="pt-6">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-red-400">{stats.failed}</p>
                    <p className="text-zinc-400 text-sm mt-1">Failed</p>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-zinc-900 border-zinc-800">
                <CardContent className="pt-6">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-amber-400">{stats.recent_7_days}</p>
                    <p className="text-zinc-400 text-sm mt-1">Last 7 Days</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card className="bg-zinc-900 border-zinc-800 mt-4">
              <CardHeader>
                <CardTitle className="text-white text-lg">Emails by Type</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-zinc-950 rounded-lg p-4">
                    <p className="text-2xl font-bold text-blue-400">{stats.by_type?.invites || 0}</p>
                    <p className="text-zinc-400 text-sm">Invites</p>
                  </div>
                  <div className="bg-zinc-950 rounded-lg p-4">
                    <p className="text-2xl font-bold text-purple-400">{stats.by_type?.tests || 0}</p>
                    <p className="text-zinc-400 text-sm">Test Emails</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="logs">
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-white">Recent Email Activity</CardTitle>
                <CardDescription className="text-zinc-400">
                  Last 50 emails sent through the platform
                </CardDescription>
              </CardHeader>
              <CardContent>
                {logsLoading ? (
                  <div className="flex justify-center py-8">
                    <RefreshCw className="w-6 h-6 animate-spin text-amber-500" />
                  </div>
                ) : logs.length === 0 ? (
                  <div className="text-center py-8 text-zinc-400">
                    <Mail className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No emails sent yet</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {logs.map((log) => (
                      <div 
                        key={log.id} 
                        className="flex items-center justify-between p-3 bg-zinc-950 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          {log.status === 'sent' ? (
                            <CheckCircle className="w-5 h-5 text-green-500" />
                          ) : (
                            <XCircle className="w-5 h-5 text-red-500" />
                          )}
                          <div>
                            <p className="text-white text-sm font-medium">{log.to_email}</p>
                            <p className="text-zinc-400 text-xs">{log.subject}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <Badge variant={log.type === 'invite' ? 'default' : 'secondary'}>
                            {log.type}
                          </Badge>
                          <p className="text-zinc-500 text-xs mt-1">
                            {formatDate(log.created_at)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Test Email Dialog */}
        <Dialog open={showTestDialog} onOpenChange={setShowTestDialog}>
          <DialogContent className="bg-zinc-900 border-zinc-800">
            <DialogHeader>
              <DialogTitle className="text-white">Send Test Email</DialogTitle>
              <DialogDescription className="text-zinc-400">
                Send a test email to verify SendGrid is working correctly
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="text-zinc-300">Recipient Email</Label>
                <Input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="test@example.com"
                  className="bg-zinc-950 border-zinc-700 text-white"
                  data-testid="test-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Subject</Label>
                <Input
                  value={testSubject}
                  onChange={(e) => setTestSubject(e.target.value)}
                  className="bg-zinc-950 border-zinc-700 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Message</Label>
                <Textarea
                  value={testMessage}
                  onChange={(e) => setTestMessage(e.target.value)}
                  rows={3}
                  className="bg-zinc-950 border-zinc-700 text-white"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowTestDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={sendTestEmail} 
                disabled={sending || !testEmail}
                className="bg-amber-500 hover:bg-amber-600"
                data-testid="confirm-send-test-btn"
              >
                {sending ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Test
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Config Dialog */}
        <Dialog open={showConfigDialog} onOpenChange={setShowConfigDialog}>
          <DialogContent className="bg-zinc-900 border-zinc-800">
            <DialogHeader>
              <DialogTitle className="text-white">Email Settings</DialogTitle>
              <DialogDescription className="text-zinc-400">
                Configure sender information for outgoing emails
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="text-zinc-300">From Email</Label>
                <Input
                  type="email"
                  value={editFromEmail}
                  onChange={(e) => setEditFromEmail(e.target.value)}
                  placeholder="support@yourdomain.com"
                  className="bg-zinc-950 border-zinc-700 text-white"
                />
                <p className="text-xs text-zinc-500">
                  Must be a verified sender in SendGrid
                </p>
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">From Name</Label>
                <Input
                  value={editFromName}
                  onChange={(e) => setEditFromName(e.target.value)}
                  placeholder="Survivor Fantasy League"
                  className="bg-zinc-950 border-zinc-700 text-white"
                />
              </div>
              <div className="bg-zinc-950 rounded-lg p-4 border border-zinc-800">
                <p className="text-sm text-zinc-400">
                  <strong className="text-amber-500">Note:</strong> The SendGrid API key is configured 
                  in environment variables and cannot be changed here. Contact your administrator 
                  to update the API key.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowConfigDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={saveConfig} 
                disabled={savingConfig}
                className="bg-amber-500 hover:bg-amber-600"
              >
                {savingConfig ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </SuperAdminLayout>
  );
}
